/**
 * How complete the profile is, for the shell: the avatar's "something is
 * missing" dot in the header and in the sidebar's account card, and the card's
 * "Perfil {pct}% completo" line.
 *
 * ── ONE RULE, ONE FILE ─────────────────────────────────────────────────────
 * The percentage is not computed here. It is the profile screen's own
 * `completeness()` over the profile screen's own `bodyFatState()`
 * (`features/profile/sections/completeness.ts`), so the header and the profile
 * can never disagree and the 31-day bound is written down once.
 *
 * ── THE THREE READS ARE THE SAME KEYS THE PROFILE USES ─────────────────────
 * `GET /me/onboarding` is the session gate's, app-wide
 * (`auth/session-gate.tsx`); the `body_fat` series and
 * `GET /me/body-assessments` are the profile's
 * (`profile/sections/use-profile-data.ts`). One cache entry each, shared —
 * never a second key, because the ring and the profile screen must be unable
 * to disagree.
 *
 * ⚠️ The body-assessments read IS a real request on every screen, where the
 * other two were already in flight for other reasons. It is the price of
 * `D05`: without it the ring keeps asking for body fat after a photo
 * assessment has answered, until something else happens to refetch — the exact
 * nagging the decision exists to stop. A 4xx is not retried
 * (`data/query-client.ts`), so a server that does not serve the route yet
 * costs one request per boot and the ring behaves exactly as it did before.
 *
 * {@link profileCompletenessOf} is the whole decision and takes no hooks, so
 * it is tested without a React tree.
 */
import { bodyAssessmentsOptions } from "@repo/client/data/body";
import {
  measurementsOptions,
  onboardingProgressOptions,
} from "@repo/client/data/profile";
import type {
  MeasurementListOutput,
  OnboardingStep,
} from "@repo/contract/training";
import { useMemo } from "react";
import { useApi } from "../../data/api";
import { useDataQuery } from "../../data/query-client";
import { photoLatestAt } from "../body/photo-series";
import { MEASUREMENT_KINDS } from "../profile/editors";
import { bodyFatState, completeness } from "../profile/sections/completeness";

/** `null` while the answer is not known yet — the dot never guesses. */
export interface ProfileCompleteness {
  readonly pct: number;
}

export function profileCompletenessOf(input: {
  /** The manual `body_fat` series; `null` while unread or never written. */
  readonly bodyFat: Pick<MeasurementListOutput, "latest"> | null;
  /** `null` while `GET /me/onboarding` has not answered. */
  readonly completed: readonly OnboardingStep[] | null;
  readonly now: Date;
  /** When the newest finished photo assessment was taken (`T109`). */
  readonly photoLatestAt: string | null;
}): ProfileCompleteness | null {
  if (input.completed === null) {
    return null;
  }
  const { pct } = completeness({
    bodyFat: bodyFatState(input.bodyFat, input.now, input.photoLatestAt),
    completed: input.completed,
  });
  return { pct };
}

export function useProfileCompleteness(): ProfileCompleteness | null {
  const api = useApi();
  const onboarding = useDataQuery(onboardingProgressOptions(api));
  const bodyFat = useDataQuery(
    measurementsOptions(api, { kind: MEASUREMENT_KINDS.bodyFat.kind })
  );
  const photos = useDataQuery(bodyAssessmentsOptions(api));
  const completed = onboarding.data?.completed ?? null;
  const series = bodyFat.data ?? null;
  const items = photos.data?.items;
  return useMemo(
    () =>
      profileCompletenessOf({
        bodyFat: series,
        completed,
        now: new Date(),
        photoLatestAt: photoLatestAt(items ?? []),
      }),
    [completed, items, series]
  );
}
